import React, { useState } from 'react';
import { useAppSelector } from 'hooks';
import { Card, CardBody, CardHeader, Col, Collapse, Row } from 'reactstrap';
import AppointmentActivitiesMapModal from '~appointments/AppointmentActivitiesMapModal';

import { formatDateString, formatDayString, formatDayTimeString, formatTimeStringAMPM } from '~libs/dayjs';

import { Button } from '~components';
import Icon, { faChevronDown, faChevronUp } from '~components/icon';

import { selectAppointmentActivities } from '~appointments/selectors/activities';

interface Props {
  className: string;
}

function AppointmentActivities({ className }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [mapCoords, setMapCoords] = useState<{ lat: number; lng: number } | null>();
  const [mapTitle, setMapTitle] = useState<string>('');
  const rows = useAppSelector(selectAppointmentActivities);

  // Build sub-section of log details based on array of object values
  const buildDetails = (details: Array<{ field: string; from: string; to: string } | string>) =>
    details.map((data, index) => (
      <Row key={index} className="ms-4 mb-1">
        {typeof data === 'object' ? `${data.field}: "${data.from}" to "${data.to}"` : data}
      </Row>
    ));

  // Build rows of activities based on appointment activities within reducer.
  const buildRows = (day: string) => {
    const dayRows = rows.filter(({ date_time }) => day === formatDayString(date_time));
    return dayRows.map(({ date_time, details, type, user, coords }, index) => (
      <Row key={index} className="mb-1">
        <Col sm={1}>{formatTimeStringAMPM(date_time)}</Col>

        <Col sm={3}>
          {user?.full_name ?? ''}{' '}
          {coords && (
            <Button
              size="sm"
              className="mx-1"
              onClick={() => {
                setShowMapModal(true);
                setMapCoords({ lat: coords.latitude, lng: coords.longitude });
                setMapTitle(`Activity location at ${formatDayTimeString(date_time)}`);
              }}
            >
              View On Map
            </Button>
          )}
        </Col>
        <Col>
          <Row className="mb-1">{type}</Row>
          {details ? buildDetails(details) : ''}
        </Col>
      </Row>
    ));
  };

  // Create group of unique "days" to group activities together
  const dates = [...new Set(rows.map(({ date_time }) => formatDateString(date_time)))];
  const bodyRows = dates.map((date, index) => (
    <div key={index}>
      <Row className="fw-bold mb-1">{date}</Row>
      {buildRows(date)}
    </div>
  ));

  return (
    <div className={className}>
      <Card className="rounded-0">
        <CardHeader
          className="d-flex justify-content-between border-0 fw-bold"
          type={'button'}
          onClick={() => setIsOpen(!isOpen)}
        >
          <div>Activity Logs</div>
          <Icon icon={isOpen ? faChevronUp : faChevronDown} className="ms-2" />
        </CardHeader>
        <Collapse isOpen={isOpen}>
          <CardBody>{bodyRows}</CardBody>
        </Collapse>
      </Card>
      <AppointmentActivitiesMapModal
        mapCoords={mapCoords}
        mapTitle={mapTitle}
        setMapCoords={setMapCoords}
        setMapTitle={setMapTitle}
        setShowMapModal={setShowMapModal}
        showMapModal={showMapModal}
      />
    </div>
  );
}

export default AppointmentActivities;
